import {
	Component,
	OnInit,
	OnDestroy,
	HostBinding,
	ElementRef,
	ViewEncapsulation,
	ChangeDetectionStrategy,
	input,
	inject,
	signal,
	computed
} from '@angular/core';
import { NHATestDirective } from '@nha/directives';
import { IFilter, IFilterComponentRef, IFilterItem, IFiltersConfig } from '@nha/components/filters/interfaces';
import { NHASelectedFilterChipComponent } from '../selected-filter-chip/selected-filter-chip.component';
import { NHAFilterComponent } from '../filter/filter.component';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BaseComponent } from '@nha/components/base.component';
import { FilterStateService } from '../../state/filter-state.service';
import { NHAIconButtonComponent } from '@nha/components/icon-button';
// import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { NHAFilterChipComponent } from '../filter-chip/filter-chip.component';
import { NHAFilterChipGroupComponent } from '../filter-chip-group/filter-chip-group.component';
import { MatTooltip } from '@angular/material/tooltip';
import { GoogleTagManagerService } from '@nha/analytics/google-tag-manager.service';

export interface IFilterBlock {
	key: string;
	isGroup: boolean;
	groupTitle?: string;
	displayOrder: number;
}

@Component({
	selector: 'nha-filters',
	templateUrl: './filters.component.html',
	styleUrls: ['./filters.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatSidenavModule,
		NHATestDirective,
		MatButtonModule,
		MatIconModule,
		CommonModule,
		NHAFilterComponent,
		NHASelectedFilterChipComponent,
		NHAIconButtonComponent,
		NHAFilterChipComponent,
		NHAFilterChipGroupComponent,
		MatTooltip
	],
	providers: []
})
export class NHAFiltersComponent extends BaseComponent implements OnInit, OnDestroy {
	private readonly el = inject(ElementRef);

	private readonly filterState = inject(FilterStateService);
	private readonly _gtmService = inject(GoogleTagManagerService);

	@HostBinding('attr.nhaTest') protected readonly _testDir: NHATestDirective = new NHATestDirective(this.el);

	key = input.required<string>();
	config = input.required<IFiltersConfig>();
	customFilterComponents = input<IFilterComponentRef[]>([]);
	hideFilters = input<boolean>(false);
	initializeFilters = input<boolean>(true);
	counts = input<boolean>(false);

	expandedGroups = signal<string[]>([]);

	filterInstance = computed(() => this.filterState.getFilterInstance(this.key())());
	visibleFilters = computed(() => this.filterState.getVisibleFilters(this.key())());
	selectedFilters = computed(() => this.filterState.getSelectedFilters(this.key())());
	closed = computed(() => this.filterState.getClosed(this.key())());
	loaded = computed(() => this.filterState.getLoaded(this.key())());
	locked = computed(() => this.filterState.getLocked(this.key())());
	canSwitchView = computed(() => this.config()?.canSwitchView ?? false);
	hasGroups = computed(() => this.filterState.getHasGroups(this.key())());
	filterType = computed(() => {
		const localType = this.localFilterType();
		if (localType) {
			return localType;
		}

		return this.filterState.getFilterType(this.key())();
	});

	localFilterType = signal<'sidenav' | 'chip' | null>(null);
	popupOpen = signal<boolean>(false);

	orderedFilterBlocks = computed<IFilterBlock[]>(() => {
		const blocks: IFilterBlock[] = [];
		const groupings = this.filterState.getFilterGroupings(this.key())();
		const nonGroupedFilters = this.visibleFilters().filter((f) => groupings?.every((g) => !g.filterKeys.includes(f.key)) ?? true);

		for (let i = 0; i < (groupings?.length ?? 0) + nonGroupedFilters.length; i++) {
			const groupAtIndex = groupings?.find((g) => g.displayOrder === i);
			if (groupAtIndex) {
				blocks.push({ key: groupAtIndex.groupTitle, isGroup: true, groupTitle: groupAtIndex.groupTitle, displayOrder: i });
			} else {
				const filterIndex = i - blocks.filter((b) => b.isGroup).length;
				const filterAtIndex = nonGroupedFilters[filterIndex];
				if (filterAtIndex) {
					blocks.push({ key: filterAtIndex.key, isGroup: false, displayOrder: i });
				}
			}
		}

		return blocks;
	});

	ngOnInit(): void {
		const key = this.key();
		const config = this.config();

		if (!key || !config) {
			return;
		}

		this.filterState.addFilterInstance(key, config, this.initializeFilters());

		this._testDir.setTestAttribute();
	}

	ngOnDestroy(): void {
		this.filterState.deInitializeFilter(this.key());
	}

	setClosed(): void {
		this.filterState.toggleClosed(this.key());
	}

	getFiltersByKey(key: string, isGroup: boolean): { value: IFilter; key: string }[] {
		const filters = this.visibleFilters();

		if (isGroup) {
			const groupings = this.filterState.getFilterGroupings(this.key())();
			const grouping = groupings?.find((g) => g.groupTitle === key);

			return grouping ? filters.filter((f) => grouping.filterKeys.includes(f.key)) : [];
		} else {
			return filters.filter((f) => f.key === key);
		}
	}

	reset(): void {
		this.filterState.resetFilter(this.key());
	}

	getCustomFilter(key: string): IFilterComponentRef {
		return this.customFilterComponents().find((cf) => cf.filterName === key);
	}

	deselectFilterItem(filterKey: string, item: IFilterItem): void {
		const filterMap = this.filterState.getFilterMap(this.key())();
		if (!filterMap) {
			return;
		}

		const filter = filterMap[filterKey];
		if (!filter) {
			return;
		}

		const updatedFilter = {
			...filter,
			items: filter.items.map((i) => (i.id === item.id ? { ...i, selected: false } : i))
		};

		this.filterState.updateFilter(this.key(), filterKey, updatedFilter);
	}

	deselectAllFilterItems(filterKey: string): void {
		const filterMap = this.filterState.getFilterMap(this.key())();
		if (!filterMap) {
			return;
		}

		const filter = filterMap[filterKey];
		if (!filter) {
			return;
		}

		const updatedFilter = {
			...filter,
			items: filter.items.map((item) => ({ ...item, selected: false }))
		};

		this.filterState.updateFilter(this.key(), filterKey, updatedFilter);
	}

	toggleGroupExpand(groupTitle: string): void {
		this.expandedGroups.update((groups) => {
			if (groups.includes(groupTitle)) {
				return groups.filter((g) => g !== groupTitle);
			} else {
				return [...groups, groupTitle];
			}
		});
	}

	changeFilterType(type: 'sidenav' | 'chip'): void {
		this.localFilterType.set(type);

		this._gtmService.pushTag({ event: 'filter_display_type_changed', filter_display_type_changed: type });
	}
}
